import { layoutNodes } from "@/app/(v2)/workflows/_hooks/useLayout";
import { getWorkflowVerticalGap, readStoredCompactViewPreference } from "@/lib/workflow/layoutPolicy";
import type { Edge, Node } from "@xyflow/react";

/**
 * Calculate optimal node positions using a tree layout algorithm
 * This creates a clean vertical flow with proper spacing for branches
 *
 * @param nodes - The workflow nodes with their children
 * @param rootId - The ID of the root node (default: "root")
 * @param measuredHeights - Optional map of node ID to measured height for dynamic spacing
 * @param minGap - Minimum gap between bottom of one node and top of next (default: 80)
 */
export function calculateTreeLayout(
  nodes: Record<string, { children?: string[] | null }>,
  rootId: string = "root",
  measuredHeights?: Map<string, number>,
  minGap: number = 80,
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  const xSpacing = 350; // Horizontal spacing between sibling branches
  const defaultNodeHeight = 120; // Default height when not measured

  // Track the next available x position at each level
  const levelXOffset = new Map<number, number>();

  // Track cumulative Y position for each branch path
  const nodeYPositions = new Map<string, number>();

  function layoutNode(nodeId: string, parentY: number, parentHeight: number): { minX: number; maxX: number } {
    const node = nodes[nodeId];
    if (!node) return { minX: 0, maxX: 0 };

    const children = node.children || [];
    const nodeHeight = measuredHeights?.get(nodeId) || defaultNodeHeight;

    // Calculate Y position: parent's Y + parent's height + gap
    const yPosition = parentY + parentHeight + minGap;
    nodeYPositions.set(nodeId, yPosition);

    if (children.length === 0) {
      // Leaf node - assign next available x position
      // Find the depth by counting ancestors (for x positioning)
      let depth = 0;
      let currentId = nodeId;
      for (const [id, nodeData] of Object.entries(nodes)) {
        if (nodeData.children?.includes(currentId)) {
          depth++;
          currentId = id;
        }
      }

      const currentX = levelXOffset.get(depth) || 0;
      positions.set(nodeId, { x: currentX, y: yPosition });
      levelXOffset.set(depth, currentX + xSpacing);
      return { minX: currentX, maxX: currentX };
    }

    // Layout children first (post-order traversal)
    const childBounds: Array<{ minX: number; maxX: number }> = [];
    for (const childId of children) {
      const bounds = layoutNode(childId, yPosition, nodeHeight);
      childBounds.push(bounds);
    }

    // Position this node centered above its children
    const minX = childBounds[0].minX;
    const maxX = childBounds[childBounds.length - 1].maxX;
    const centerX = (minX + maxX) / 2;

    positions.set(nodeId, { x: centerX, y: yPosition });
    return { minX, maxX };
  }

  // Start layout from root at Y=0 with 0 parent height (so first node is at minGap from top)
  const rootNode = nodes[rootId];
  if (rootNode) {
    const rootHeight = measuredHeights?.get(rootId) || defaultNodeHeight;
    positions.set(rootId, { x: 0, y: 0 });

    const children = rootNode.children || [];
    const childBounds: Array<{ minX: number; maxX: number }> = [];

    for (const childId of children) {
      const bounds = layoutNode(childId, 0, rootHeight);
      childBounds.push(bounds);
    }

    // Re-center root if it has children
    if (childBounds.length > 0) {
      const minX = childBounds[0].minX;
      const maxX = childBounds[childBounds.length - 1].maxX;
      const centerX = (minX + maxX) / 2;
      positions.set(rootId, { x: centerX, y: 0 });
    }
  }

  return positions;
}

/**
 * Recalculate node positions based on measured heights for proper spacing.
 * Call this after React Flow has measured node dimensions.
 * Uses the unified layoutNodes algorithm for consistent tree layout.
 *
 * @param nodes - Current React Flow nodes with measured dimensions
 * @param edges - Current React Flow edges
 * @returns Updated nodes with recalculated positions, or null if no changes needed
 */
export function recalculateNodePositions(
  nodes: Node[],
  edges: Edge[],
  isCompact: boolean = readStoredCompactViewPreference(),
): Node[] | null {
  // Filter out placeholder nodes for layout calculation
  const realNodes = nodes.filter(n => n.type !== "placeholder");

  if (realNodes.length === 0) {
    return null;
  }

  // Check if we have measured heights - if not, skip recalculation
  const hasMeasuredHeights = realNodes.some(n => n.measured?.height);
  if (!hasMeasuredHeights) {
    return null;
  }

  // Use the unified layout algorithm
  const layoutedNodes = layoutNodes(nodes, edges, { verticalGap: getWorkflowVerticalGap(isCompact) });

  // Check if positions actually changed significantly (more than 5px)
  let hasSignificantChange = false;
  for (const node of realNodes) {
    const layoutedNode = layoutedNodes.find(n => n.id === node.id);
    if (layoutedNode) {
      const yDiff = Math.abs(node.position.y - layoutedNode.position.y);
      const xDiff = Math.abs(node.position.x - layoutedNode.position.x);
      if (yDiff > 5 || xDiff > 5) {
        hasSignificantChange = true;
        break;
      }
    }
  }

  if (!hasSignificantChange) {
    return null;
  }

  return layoutedNodes;
}
